import Link from 'next/link'
import type React from 'react'
import Crafts from '../../assets/crafts.svg'
import FishOilImage from '../../assets/fish-oil.svg'
import Friendship from '../../assets/friendship.svg'
import Future from '../../assets/future.svg'
import Exercise from '../../assets/lifting.svg'
import Meditation from '../../assets/meditation.svg'
import Pills from '../../assets/pills.svg'
import Sleep from '../../assets/sleep.svg'
import SocialMediaIcon from '../../assets/social-media.svg'
import Therapy from '../../assets/therapy.svg'
import Yelling from '../../assets/yelling.svg'
import { capitalize } from '../../helpers/capitalize'
import {
  Calm,
  FishOil,
  HappinessTrap,
  Headspace,
  Zencare,
} from '../affiliates/MentalHealth'
import { Footnote } from '../curriculum/Footnote'
import { FootnoteList } from '../curriculum/FootnoteList'
import { Explanation } from '../explanation/Explanation'
import { Section } from '../guides/Section'
import { Title } from '../guides/Title'

type props = {
  condition: 'depression' | 'anxiety'
}

export const MentalHealth: React.FC<props> = ({ condition }) => {
  const adjective = condition === 'depression' ? 'depressed' : 'anxious'
  return (
    <div className={'w90 center module'}>
      <h1 className="huge">{capitalize(condition)}</h1>
      <Explanation color="blue">
        <div>
          <strong>Why: </strong>Feeling {adjective} <strong>sucks</strong>. This
          stuff below can make it better.
        </div>
      </Explanation>

      <div>
        <h2 className="mt40 mb0">Physical</h2>
        <Section>
          <Title alt="Exercise" src={Exercise} title={'Start exercising'} />
          <Explanation color="blue">
            {condition === 'depression' ? (
              <div>
                <strong>Why:</strong> Exercise, especially cardio, is the single
                most effective treatment for {condition} that researchers have{' '}
                <strong>ever</strong> found. It is <strong>three</strong> times
                more effective than antidepressants{' '}
                <FootnoteList
                  links={[
                    'https://pubmed.ncbi.nlm.nih.gov/26978184/',
                    'https://jamanetwork.com/journals/jamapsychiatry/fullarticle/2680311',
                  ]}
                />
                . It also doesn{`'`}t have their side effects.
              </div>
            ) : (
              <div>
                <strong>Why:</strong> Exercise is a reasonably effective
                treatment for {condition}{' '}
                <Footnote link="https://www.ncbi.nlm.nih.gov/pmc/articles/PMC6048763/" />
                , about as effective as an antidepressant without their side
                effects.
              </div>
            )}
          </Explanation>
          <Explanation color="green">
            <div>
              <strong>Tip:</strong> Not sure where to start? Check out our{' '}
              <Link href="/lifting">Guide to Lifting</Link> and{' '}
              <Link href="/cardio">Guide to Cardio</Link>. And don{`'`}t worry,
              these programs are designed for people who have never exercised
              before and really don{`'`}t want to. So they{`'`}re{' '}
              <strong>really</strong> easy to get started with!
            </div>
          </Explanation>
          {condition === 'depression' && (
            <Explanation color="yellow">
              <div>
                <strong>Heads up:</strong> Don{`'`}t have the energy to start a
                full exercise program? Not a problem! Walking has been shown to
                surprisingly effective in combatting depression. In fact, it is
                almost <strong>twice</strong> as effective as anti-depressants{' '}
                <Footnote
                  link={'https://www.ncbi.nlm.nih.gov/books/NBK99429/'}
                />
                .
              </div>
            </Explanation>
          )}
        </Section>

        <Section>
          <Title alt="Sleep" src={Sleep} title={'Fix your sleep'} />
          <Explanation color="blue">
            {condition === 'depression' ? (
              <div>
                <strong>Why:</strong> The link between {condition} and sleep is{' '}
                <strong>insanely</strong> strong. For example, 75% of{' '}
                {adjective} people show symptoms of insomnia{' '}
                <Footnote link="https://www.ncbi.nlm.nih.gov/pmc/articles/PMC3181883/" />
                . Thus, treating your problematic sleep is akin to treating your{' '}
                {condition}.
              </div>
            ) : (
              <div>
                <strong>Why:</strong> The link between {condition} and sleep is{' '}
                <strong>reasonably</strong> strong. For example, about 30% of{' '}
                {adjective} people show symptoms of insomnia{' '}
                <Footnote link="https://www.ncbi.nlm.nih.gov/pmc/articles/PMC3181635/" />
                . Thus, treating your problematic sleep should lower your levels
                of {condition}.
              </div>
            )}
          </Explanation>
          <Explanation color="green">
            <div>
              <strong>Tip:</strong> Not sure where to start? Check out our{' '}
              <Link href="/sleep">Guide to Sleep</Link>, as it will cover
              everything you need to know to fix your sleep!
            </div>
          </Explanation>
        </Section>

        <h2 className="mt40 mb0">Mental</h2>

        <Section>
          <Title alt="Therapist" src={Therapy} title={'Talk to a therapist'} />
          <Explanation color="blue">
            {condition === 'depression' ? (
              <div>
                <strong>Why:</strong> {capitalize(condition)} is marked by a
                pattern of self-critical and hopeless thinking that is{' '}
                <strong>really</strong> painful for the thinker.{' '}
                <strong>But you don{`'`}t have to suffer alone.</strong> A
                therapist can teach you a set of research-backed techniques that
                will make dealing with these thoughts easier.
              </div>
            ) : (
              <div>
                <strong>Why:</strong> {capitalize(condition)} is marked either
                by a phobia or a pattern of catastrophic thinking, both of which
                are <strong>really</strong> painful for the thinker.{' '}
                <strong>But you don{`'`}t have to suffer alone.</strong> A
                therapist can teach you a set of research-backed techniques that
                will make dealing with these thoughts easier.
              </div>
            )}
          </Explanation>
          <Explanation color="green">
            <div>
              <strong>Tip: </strong> If you need help finding a therapist,{' '}
              <Zencare /> is a website that makes it really easy to find
              pre-vetted therapists. You can book free initial calls, check
              availability, and more.
            </div>
          </Explanation>
          <Explanation color="yellow">
            <div>
              <strong>Heads up: </strong> If you cannot or will not see a
              therapist, check out the book <HappinessTrap />. This book is
              great. It will teach many of the skills that you would learn in
              therapy.
            </div>
          </Explanation>
        </Section>

        <Section>
          <Title
            alt="Man with binoculars"
            src={Future}
            title={'Meet your future self'}
          />
          <Explanation color="blue">
            <div>
              <strong>Why:</strong> Spending a ten-minute session writing about
              the best life you can imagine for yourself in the future may
              significantly improve your overall level of happiness{' '}
              <FootnoteList
                links={[
                  'https://www.ncbi.nlm.nih.gov/pmc/articles/PMC6524817/',
                ]}
              />
              .
            </div>
          </Explanation>
          <Explanation color="green">
            <div>
              <strong>Instructions:</strong> Set a timer or stopwatch for 10
              minutes. During this time, think about your best possible future
              self and write it down on paper. Imagine your life the way you
              always imagined it would be. Imagine that you have performed to
              the best of your abilities and achieved all the things you wanted
              to in life. While writing, do not worry about grammar or
              punctuation. Just focus on writing all your thoughts and emotions
              in an expressive way. You may want to have several sheets of paper
              for this exercise{' '}
              <Footnote link="https://positivepsychology.com/wp-content/uploads/The-Best-Possible-Self.pdf" />
              .
            </div>
          </Explanation>
        </Section>

        <h2 className="mt40 mb0">Social</h2>

        <Section>
          <Title
            alt="Friendship"
            src={Friendship}
            title={'Combat loneliness'}
          />
          <Explanation color="blue">
            <div>
              <strong>Why:</strong> Unsurprisingly, loneliness is strongly
              linked to your levels of anxiety, depression, and death{' '}
              <FootnoteList
                links={[
                  'https://pubmed.ncbi.nlm.nih.gov/25910392/',
                  'https://www.ncbi.nlm.nih.gov/pmc/articles/PMC7267797/',
                ]}
              />
              , as it increases inflammation{' '}
              <Footnote link="https://pubmed.ncbi.nlm.nih.gov/32092313/" /> and
              disregulates cortisol{' '}
              <Footnote link="https://www.ncbi.nlm.nih.gov/pmc/articles/PMC2841363/" />
              . But, importantly, loneliness is not the same thing as social
              isolation, you can feel live in relative isolation and not feel
              lonely. And it{`'`}s loneliness, not social isolation, that{`'`}s
              the killer{' '}
              <FootnoteList
                links={[
                  'https://ebmh.bmj.com/content/15/4/87',
                  'https://www.ncbi.nlm.nih.gov/pmc/articles/PMC7089537/',
                ]}
              />
              .
            </div>
          </Explanation>
          <Explanation color="green">
            <div>
              <strong>Tip: </strong> To combat loneliness, you need to do two
              things: learn to be alone without feeling lonely (next section)
              and spend more time with people (this section). For example,
              volunteer, reach out to friends and family, use a dating app, or
              sign up for a class at your community center or local college.
            </div>
          </Explanation>
          <Explanation color="pink">
            <div>
              <strong>Warning: </strong>When all else falls, many turn to
              adopting a dog or cat. Not only is this a big responsibility, it{' '}
              <strong>may not</strong> reduce your loneliness, as research is
              quite conflicted on this point{' '}
              <Footnote link="https://www.tandfonline.com/doi/abs/10.1080/08927936.2015.11435396" />
              . Pets also won{`'`}t lower your risk of heart disease, cancer, or
              death{' '}
              <FootnoteList
                links={[
                  'https://www.ncbi.nlm.nih.gov/pmc/articles/PMC6499429/',
                  'https://www.ncbi.nlm.nih.gov/pmc/articles/PMC5010503/',
                ]}
              />
              .
            </div>
          </Explanation>
        </Section>

        <Section>
          <Title alt="Paper crafts" src={Crafts} title={'Make something'} />
          <Explanation color="blue">
            <div>
              <strong>Why: </strong> Humans have three, fundamental needs:
              freedom, deep friendship, and to get better at something{' '}
              <FootnoteList
                links={[
                  'http://labs.psychology.illinois.edu/~ediener/faq.html',
                  'https://worldhappiness.report/archive/',
                  'https://link.springer.com/article/10.1007/s10902-006-9023-4',
                ]}
              />
              . The easiest way to do the final one, especially when alone, is
              to make something, whether that be art, code, or a business.
              Importantly, what you make doesn{`'`}t need to make money. It just
              needs to be something that one, you find engaging, and two, can
              done alone, as this is an extremely useful way to combat
              loneliness.
            </div>
          </Explanation>
          <Explanation color="green">
            <div>
              <strong>Tip:</strong> Not sure where to start? Check out this list
              of hobbies{' '}
              <Footnote link="https://en.wikipedia.org/wiki/List_of_hobbies" />.
            </div>
          </Explanation>
        </Section>

        <Section>
          <Title
            alt="Social media"
            src={SocialMediaIcon}
            title={'Ditch social media'}
          />
          <Explanation color="blue">
            <div>
              <strong>Why: </strong> Research has shown social media usage leads
              to increased depression, loneliness, and anxiety. This is because
              these platforms inspire <strong>FoMo</strong> (fear of missing
              out) and <strong>social comparison</strong>, where you compare
              your life to the airbrushed highlights of someone else{`'`}s life{' '}
              <Footnote link="https://www.ncbi.nlm.nih.gov/pmc/articles/PMC7345987/" />
              .
            </div>
          </Explanation>
          <Explanation color="green">
            <div>
              <strong>Tip:</strong> You <strong>do not</strong> need to ditch
              all social media platforms. Instead, you just need to avoid FoMo
              or social comparison. Following the {`"`}cool kids{`"`} and their
              lavish parties? Bad idea! Following that prominent CEO or venture
              capitalist? Bad idea! Looking at memes and cute animals? Excellent
              idea! This is easier to do with some platforms, like Reddit, than
              others, like Instagram.
            </div>
          </Explanation>
        </Section>

        <Section>
          <Title alt="Yelling" src={Yelling} title={'Leave a bad job'} />
          <Explanation color="blue">
            <div>
              <strong>Why: </strong> A bad job, one where you have minimal
              control over your time and work and the work itself isn{`'`}t
              meaningful, is strongly linked to depression and anxiety, as well
              as certain physical conditions, like heart attacks{' '}
              <FootnoteList
                links={[
                  'https://www.vox.com/the-highlight/2019/7/6/20681186/fast-food-worker-burnout',
                  'https://unhealthywork.org/classic-studies/the-whitehall-study/',
                  'https://jech.bmj.com/content/56/12/922',
                ]}
              />
              . This is because much of the cause of all these conditions,{' '}
              <strong>especially depression</strong>, is chronic inflammation
              and cortisol dysfunction{' '}
              <FootnoteList
                links={[
                  'https://www.ncbi.nlm.nih.gov/pmc/articles/PMC6987444/',
                  'https://pubmed.ncbi.nlm.nih.gov/24659553/',
                  'https://pubmed.ncbi.nlm.nih.gov/23253896/',
                  'https://jamanetwork.com/journals/jamapsychiatry/fullarticle/1485898',
                ]}
              />
              , both of which are increased by social stressors, like a bad job.
              This is why the risk of death due to a heart attack is doubled in
              someone who has recently been bereaved, and why seemingly
              unrelated things, like pollution{' '}
              <FootnoteList
                links={[
                  'https://pubmed.ncbi.nlm.nih.gov/32442925/',
                  'https://www.ncbi.nlm.nih.gov/pmc/articles/PMC6447209/',
                ]}
              />{' '}
              and diet{' '}
              <Footnote link="https://pubmed.ncbi.nlm.nih.gov/31596866/" /> are
              strongly linked to depression and anxiety, as they can increase
              inflammation and/or disregulate cortisol{' '}
              <FootnoteList
                links={[
                  'https://pubmed.ncbi.nlm.nih.gov/31447226/',
                  'https://pubmed.ncbi.nlm.nih.gov/17463411/',
                ]}
              />
              .
            </div>
          </Explanation>
          <Explanation color="green">
            <div>
              <strong>Tip: </strong>
              The best way to get a good job is to master a rare and valuable
              skill, called career capital, which you can trade to an employer
              for stuff like higher pay and more control over your time and work{' '}
              <Footnote link="https://80000hours.org/career-guide/career-capital/" />
              .
            </div>
          </Explanation>
        </Section>

        <h2>Optional</h2>

        <Section>
          <Title alt="Meditation" src={Meditation} title={'Meditation'} />
          <Explanation color="blue">
            <div>
              <strong>Why:</strong> Meditation is an effective way to reduce
              your levels of depression and anxiety. It is makes you happier{' '}
              <Footnote link="https://www.nytimes.com/2003/02/04/health/behavior-finding-happiness-cajole-your-brain-to-lean-to-the-left.html" />
              , more empathetic{' '}
              <Footnote link="https://www.theatlantic.com/health/archive/2015/07/mindfulness-meditation-empathy-compassion/398867/" />
              , and is protective against loneliness{' '}
              <FootnoteList
                links={[
                  'https://www.pnas.org/content/116/9/3488',
                  'https://bmcpsychology.biomedcentral.com/articles/10.1186/s40359-021-00585-8',
                ]}
              />
              . <strong>However</strong>, meditation requires a decent chunk of
              time, around 30 minutes per day{' '}
              <Footnote link="https://news.harvard.edu/gazette/story/2011/01/eight-weeks-to-a-better-brain/" />
              , and it{`'`}s not for everyone. We{`'`}d <strong>much</strong>{' '}
              prefer people start with therapy and labeling, and then only move
              to meditation once they{`'`}re ready for it.
            </div>
          </Explanation>
          <Explanation color="green">
            <div>
              <strong>Tip:</strong> Want to learn to meditate? Check out{' '}
              <a href="https://www.reddit.com/r/Meditation/wiki/faq">this</a>{' '}
              excellent, free guide on Reddit, then this{' '}
              <a href="https://www.lionsroar.com/entering-the-jhanas/">post</a>{' '}
              and <a href="https://www.reddit.com/r/streamentry/">guide</a> when
              you become more advanced. You can also download a meditation app.
              We like <Headspace /> and <Calm />.
            </div>
          </Explanation>
        </Section>

        <Section>
          <Title alt="Pills" src={Pills} title={'Antidepressants'} />
          <Explanation color="blue">
            <div>
              <strong>Why:</strong> Antidepressants, namely SNRIs and SSRIs,
              work by restoring the brain to a more changeable (plastic) state{' '}
              <Footnote link="https://www.ncbi.nlm.nih.gov/pmc/articles/PMC6174980/" />
              , rather than restoring low levels of a neurotransmitter, like
              serotonin{' '}
              <Footnote link="https://themighty.com/2019/12/is-depression-caused-by-chemical-imbalance/" />
              . They have been shown to be <strong>reasonably</strong> effective
              in treating {condition}{' '}
              <Footnote link="https://www.ncbi.nlm.nih.gov/pmc/articles/PMC3918025/" />
              , especially when combined with therapy. However, they{`'`}re not
              magic and do come with a set of minor side effects. We think they
              {`'`}re worth it for most people suffering from {condition}, but
              talk with your doctor to learn more.
            </div>
          </Explanation>
        </Section>

        <Section>
          <Title alt="Fish oil" src={FishOilImage} title={'Fish oil'} />
          <Explanation color="blue">
            <div>
              <strong>Why:</strong> Fish oil has been shown to be{' '}
              <strong>reasonably</strong> effective in treating anxiety{' '}
              <Footnote
                link={
                  'https://jamanetwork.com/journals/jamanetworkopen/fullarticle/2702216'
                }
              />{' '}
              and depression{' '}
              <Footnote
                link={'https://www.ncbi.nlm.nih.gov/pmc/articles/PMC6683166/'}
              />
              , roughly comparable with antidepressants. An effective dose is
              probably 2,000 mg of combined EPA and DHA, with an EPA:DHA ratio
              of at least 2:1. We like <FishOil text="this brand" />.
            </div>
          </Explanation>
        </Section>
      </div>
    </div>
  )
}
